import { useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "@/common/hooks/useRedux";
import {
    acceptPendingDocument,
    clearPendingDocuments,
    getActiveDocument,
    getActiveDocumentIndex,
    getActiveTabId,
    getPendingDocument,
    setPendingDocument,
    setScoutDocumentStatus,
} from "@/features/patient-view/state/redux/activeDocumentsSlice";
import { ChatJson, ChatMessagePossiblyOptimistic } from "@/common/utils/types";
import { useScoutChatHistories } from "@/state/hooks/useScoutChat";
import { LLMResponse, processScoutRequest } from "@/common/utils/scoutRequestProcessor";
import { getSyncEntry, subscribe } from "@/features/patient-view/components/documents/editor/provider/documentSync";

/**
 * Hook to handle Scout document editing functionality.
 * Manages document accept/reject/retry actions and pre/post send document handling.
 * @returns Object containing document handling functions
 */
export const useScoutDocumentHandling = () => {
    const dispatch = useAppDispatch();
    const activeDocument = useAppSelector(getActiveDocument);
    const activeTabId = useAppSelector(getActiveTabId);
    const activeDocumentIndex = useAppSelector(getActiveDocumentIndex);
    const activeDocumentId = activeDocument?.id;
    const pendingDocument = useAppSelector(getPendingDocument);

    const {
        query: { data: scoutChatHistories },
        addMessageMutation,
        updateMessageMutation,
        updateMessagesMutation,
    } = useScoutChatHistories();
    const activeChatHistoryId = scoutChatHistories?.[0]?.id;

    const cancelPendingDocumentActions = useCallback(async () => {
        dispatch(clearPendingDocuments());
        updateMessagesMutation.mutate({
            chatHistoryId: activeChatHistoryId,
            match: { actionStatus: "pending" },
            update: { actionStatus: "cancelled" },
        });
    }, [dispatch, activeChatHistoryId, updateMessagesMutation]);

    // If the active document context has changed, or the view remounted,
    // cancel any pending message actions as they are no longer usable
    useEffect(() => {
        if (activeChatHistoryId) {
            cancelPendingDocumentActions();
        }
    }, [activeDocumentId, activeChatHistoryId]);

    // EN-1253: a proposal is a copy of the whole document taken when it was requested. If a
    // "Use this version" lands after that, the proposal still holds the text the user discarded,
    // the editor cannot show "theirs" while the proposal owns it (the reset is owed), and accepting
    // it later, after a remount, would resend the discarded text over the other device's version.
    // Cancel such a proposal; the editor then re-bases on theirs, as on a reject. A proposal
    // requested after the owed reset was set is built on theirs and is kept.
    const hasPendingRef = useRef(false);
    hasPendingRef.current = !!pendingDocument;
    const owedAtRequestRef = useRef<ChatJson | undefined>(undefined);
    const cancelRef = useRef(cancelPendingDocumentActions);
    cancelRef.current = cancelPendingDocumentActions;
    useEffect(() => {
        if (!activeDocumentId) return undefined;
        const check = () => {
            const owed = getSyncEntry(activeDocumentId)?.owedTheirsReset;
            if (!hasPendingRef.current || !owed || owed === owedAtRequestRef.current) return;
            hasPendingRef.current = false;
            void cancelRef.current();
            toast.info(
                "Scout's suggestion was cleared because you chose the other device's version. Ask Scout again if you still need it.",
            );
        };
        check();
        return subscribe(activeDocumentId, check);
    }, [activeDocumentId, pendingDocument]);

    /**
     * Handles pre-send document logic before calling processScoutRequest.
     * Sets the document status to processing.
     */
    const handlePreSendDocumentActions = useCallback(() => {
        // EN-1253: the reset owed when this request is built; a later one makes its proposal stale.
        owedAtRequestRef.current = activeDocumentId ? getSyncEntry(activeDocumentId)?.owedTheirsReset : undefined;
        dispatch(
            setScoutDocumentStatus({
                tabId: activeTabId,
                documentIndex: activeDocumentIndex,
                status: "processing",
            }),
        );
    }, [dispatch, activeTabId, activeDocumentIndex, activeDocumentId]);

    /**
     * Handles post-send document logic after receiving processScoutRequest response.
     * Sets pending document changes and updates document status based on response.
     * @param response - The response from processScoutRequest
     * @returns Whether an action is required (document edit pending)
     */
    const handleSendReponseDocumentActions = useCallback(
        (response: LLMResponse): boolean => {
            let isActionRequired = false;

            if (response.success && response.intent === "edit-document" && activeDocument) {
                isActionRequired = true;
                dispatch(
                    setPendingDocument({
                        tabId: activeTabId,
                        documentIndex: activeDocumentIndex,
                        pendingDocumentChanges: response.pendingDocumentChanges,
                    }),
                );
                dispatch(
                    setScoutDocumentStatus({
                        tabId: activeTabId,
                        documentIndex: activeDocumentIndex,
                        status: "pending",
                    }),
                );
            } else {
                dispatch(
                    setScoutDocumentStatus({
                        tabId: activeTabId,
                        documentIndex: activeDocumentIndex,
                        status: undefined,
                    }),
                );
            }

            return isActionRequired;
        },
        [dispatch, activeTabId, activeDocumentIndex, activeDocument],
    );

    /**
     * Handles clearing document status on error.
     */
    const handleDocumentError = useCallback(() => {
        dispatch(
            setScoutDocumentStatus({
                tabId: activeTabId,
                documentIndex: activeDocumentIndex,
                status: undefined,
            }),
        );
    }, [dispatch, activeTabId, activeDocumentIndex]);

    /**
     * Handles the accept action for document changes.
     * Accepts the pending document and updates the message status.
     * @param message - The chat message containing the action to accept
     */
    const handleAcceptDocumentAction = useCallback(
        async (message: ChatMessagePossiblyOptimistic) => {
            dispatch(
                acceptPendingDocument({
                    tabId: activeTabId,
                    documentIndex: activeDocumentIndex,
                }),
            );
            dispatch(
                setScoutDocumentStatus({
                    tabId: activeTabId,
                    documentIndex: activeDocumentIndex,
                    status: undefined,
                }),
            );
            updateMessageMutation.mutate({
                chatHistoryId: activeChatHistoryId,
                messageId: message.id,
                message: { actionStatus: "accepted" as const },
            });
        },
        [dispatch, activeTabId, activeDocumentIndex, updateMessageMutation, activeChatHistoryId],
    );

    /**
     * Handles the reject action for document changes.
     * Clears the pending document and updates the message status.
     * @param message - The chat message containing the action to reject
     */
    const handleRejectDocumentAction = useCallback(
        async (message: ChatMessagePossiblyOptimistic) => {
            dispatch(
                setPendingDocument({
                    tabId: activeTabId,
                    documentIndex: activeDocumentIndex,
                    pendingDocumentChanges: undefined,
                }),
            );
            dispatch(
                setScoutDocumentStatus({
                    tabId: activeTabId,
                    documentIndex: activeDocumentIndex,
                    status: undefined,
                }),
            );
            updateMessageMutation.mutate({
                chatHistoryId: activeChatHistoryId,
                messageId: message.id,
                message: { actionStatus: "rejected" as const },
            });
        },
        [dispatch, activeTabId, activeDocumentIndex, updateMessageMutation, activeChatHistoryId],
    );

    /**
     * Handles the retry action for document changes.
     * Reprocesses the pending user input with processScoutRequest.
     * @param messageId - The ID of the message to retry
     * @param processScoutRequest - The function to process scout requests
     */
    const handleRetryDocumentAction = useCallback(
        async (messageId: string, pendingUserInput: string) => {
            if (!pendingUserInput) return;

            try {
                // Hide the action buttons and show retrying status
                updateMessageMutation.mutate({
                    chatHistoryId: activeChatHistoryId,
                    messageId: messageId,
                    message: { actionStatus: "retrying" as const },
                });
                dispatch(
                    setScoutDocumentStatus({
                        tabId: activeTabId,
                        documentIndex: activeDocumentIndex,
                        status: "processing",
                    }),
                );

                const response = await processScoutRequest(pendingUserInput, activeDocument);
                let isActionRequired = false;
                if (response.success && response.intent === "edit-document") {
                    isActionRequired = true;
                    dispatch(
                        setPendingDocument({
                            tabId: activeTabId,
                            documentIndex: activeDocumentIndex,
                            pendingDocumentChanges: response.pendingDocumentChanges,
                        }),
                    );
                    dispatch(
                        setScoutDocumentStatus({
                            tabId: activeTabId,
                            documentIndex: activeDocumentIndex,
                            status: "pending",
                        }),
                    );
                } else {
                    dispatch(
                        setScoutDocumentStatus({
                            tabId: activeTabId,
                            documentIndex: activeDocumentIndex,
                            status: undefined,
                        }),
                    );
                }

                // Update the original message to show "Retried" status
                updateMessageMutation.mutate({
                    chatHistoryId: activeChatHistoryId,
                    messageId: messageId,
                    message: { actionStatus: null, retriedAt: new Date() },
                });
                // Add the LLM response message
                addMessageMutation.mutate({
                    chatHistoryId: activeChatHistoryId,
                    message: {
                        text: response.message,
                        isUser: false,
                        timestamp: new Date(),
                        actionIntent: response.success ? response.intent : undefined,
                        actionStatus: isActionRequired ? "pending" : null,
                    },
                });
            } catch {
                // Update the original message to show "Retry Failed" status
                updateMessageMutation.mutate({
                    chatHistoryId: activeChatHistoryId,
                    messageId: messageId,
                    message: { actionStatus: null, retryFailedAt: new Date() },
                });
                // Add the error message
                addMessageMutation.mutate({
                    chatHistoryId: activeChatHistoryId,
                    message: {
                        text: "Sorry, I encountered an error processing your request. Please try again.",
                        isUser: false,
                        timestamp: new Date(),
                        actionStatus: null,
                    },
                });

                dispatch(
                    setScoutDocumentStatus({
                        tabId: activeTabId,
                        documentIndex: activeDocumentIndex,
                        status: undefined,
                    }),
                );
            }
        },
        [
            dispatch,
            activeTabId,
            activeDocumentIndex,
            activeDocument,
            activeChatHistoryId,
            updateMessageMutation,
            addMessageMutation,
        ],
    );

    return {
        handleAcceptDocumentAction,
        handleRejectDocumentAction,
        handleRetryDocumentAction,
        handlePreSendDocumentActions,
        handleSendReponseDocumentActions,
        handleDocumentError,
        cancelPendingDocumentActions,
    };
};
